import React from 'react';
import moment from 'moment';
import max from 'lodash/max';
import round from 'lodash/round';
import GeneralChart from '@/components/job-title/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { JobTitleContext } from '@/context/create-context';
import { numberToUpper } from '@/utils/numbers';

/**
 * AMOUNT_GAP_RATIO: gaps between two graphs.
 * AMOUNT_LESS_THAN_MIN_RATIO: lower bound of the graph, which is slightly less than the min
 * AMOUNT_MORE_THAN_MAX_RATIO: upper bound of the graph, which is slightly more than the max
 */
export const AMOUNT_GAP_RATIO = 0.05;
export const AMOUNT_LESS_THAN_MIN_RATIO = 0.75;
export const AMOUNT_MORE_THAN_MAX_RATIO = 1.25;

const JobFutureAmountCard = () => {
  const {
    jobTitle: {
      job_open_details: {
        new_job_details: {
          by_month: newJobDetailsByMonth,
          salary_min_of_all_period: salaryMinOfAllPeriod,
        },
      },
      city_name: cityName = '',
    },
    displayName,
  } = React.useContext(JobTitleContext);
  const lineChartRef = React.useRef(null);

  const seoValue = React.useMemo(() => {
    const newJobDetailsByMonthLength = newJobDetailsByMonth.length;
    const lastMonthNewJobDetail =
      newJobDetailsByMonth[newJobDetailsByMonthLength - 1];
    const [lastYear, lastMonth] = lastMonthNewJobDetail.name.split('-');
    const [secondLastYear, secondLastMonth] = moment(
      newJobDetailsByMonth[
        newJobDetailsByMonthLength > 1
          ? newJobDetailsByMonthLength - 2
          : newJobDetailsByMonthLength - 1
      ].name,
    )
      .format('yyyy-M')
      .split('-');
    const growthRate = round(
      (lastMonthNewJobDetail?.growth_rate || NaN) * 100,
      1,
    );
    const data: string[] = [];
    let defaultYear: string;
    newJobDetailsByMonth.slice(-7).map((item) => {
      const [year, month] = moment(item.name).format('yyyy年-M月').split('-');
      data.push(
        `${year !== defaultYear ? year : ''}${month}新增${item.value}个职位`,
      );
      if (year !== defaultYear) defaultYear = year;
    });
    return {
      data,
      lastYear,
      secondLastYear,
      lastMonth,
      secondLastMonth,
      growthRate,
      lastMonthNewJobDetail,
    };
  }, [newJobDetailsByMonth]);

  const lineConfig = React.useMemo<ECOption>(() => {
    const salaryMaxOfAllPeriod =
      max(newJobDetailsByMonth.map((item) => item.value)) || NaN;
    const gap =
      (salaryMaxOfAllPeriod * AMOUNT_MORE_THAN_MAX_RATIO -
        salaryMinOfAllPeriod * AMOUNT_LESS_THAN_MIN_RATIO) *
      AMOUNT_GAP_RATIO;
    const chartData = newJobDetailsByMonth.map(({ name, value }) => ({
      category: name,
      value,
    }));
    const chartGapData = newJobDetailsByMonth.map(({ name, value }) => ({
      category: name,
      value: value - gap,
    }));

    return {
      grid: { show: false, top: '1%', bottom: '10%', left: '2%', right: '2%' },
      xAxis: {
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: 'rgba(0,0,0,0.85)' },
        data: chartData.map((item) => item.category),
      },
      yAxis: {
        type: 'value',
        show: false,
        min: salaryMinOfAllPeriod * AMOUNT_LESS_THAN_MIN_RATIO,
        max: salaryMaxOfAllPeriod * AMOUNT_MORE_THAN_MAX_RATIO,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#fff', type: 'solid' },
        },
        formatter: '{c}个',
        padding: 0,
        borderWidth: 0,
        backgroundColor: 'transparent',
        shadowColor: 'transparent',
        position: (point) => [point[0] - 30, point[1] - 25],
        textStyle: {
          fontSize: 13,
          fontWeight: 500,
          color: '#008489',
          textAlign: 'center',
        },
      },
      series: [
        {
          type: 'line',
          data: chartData,
          smooth: true,
          cursor: 'default',
          emphasis: { disabled: true },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#008489',
          },
        },
        {
          type: 'line',
          data: chartGapData,
          smooth: true,
          cursor: 'default',
          emphasis: { disabled: true },
          itemStyle: {
            color: 'transparent',
            opacity: 0,
          },
          tooltip: { show: false },
          areaStyle: {
            origin: 'start',
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: '#bde0e1',
                },
                {
                  offset: 1,
                  color: '#eef7f7',
                },
              ],
            },
          },
        },
      ],
    };
  }, [newJobDetailsByMonth, salaryMinOfAllPeriod]);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}${displayName}每月新增多少职位？`}
      subTitle="新增职位数"
      description={
        <DataInterpretationSEO
          lineClamp="line-clamp-5 tablet:line-clamp-3"
          className="font-normal text-opacity-85"
        >
          {`${cityName}${displayName}近${numberToUpper(
            seoValue.data.length,
          )}个月中每个月新增开放多少职位数？${seoValue.data.join('，')}。${
            seoValue.lastYear !== seoValue.secondLastYear &&
            seoValue.lastMonth !== seoValue.secondLastMonth
              ? `${seoValue.lastYear}年${moment()
                  .month(Number(seoValue.lastMonth) - 1)
                  .format('M月')}新增职位数相较于${
                  seoValue.secondLastYear !== seoValue.lastYear
                    ? `${seoValue.secondLastYear}年`
                    : ''
                }${seoValue.secondLastMonth}月${
                  seoValue.growthRate > 0 ? '增长' : '减少'
                }了${Math.abs(
                  seoValue.growthRate,
                )}%，${cityName}${displayName}的职位增长率在同类职位中排名第${
                  seoValue.lastMonthNewJobDetail.growth_rate_rank
                }位。`
              : ''
          }数据更新时间截止${seoValue.lastYear}年${
            seoValue.lastMonth
          }月，曲线越向上代表市场需求量越大，就业情况相对较好。数据依赖于各平台公开薪酬数据，时间不同可能会产生波动，仅供参考。`}
        </DataInterpretationSEO>
      }
    >
      <div className="h-52 mb-4 mx-5 [&_canvas]:cursor-default tablet:mx-0 tablet:h-72">
        <CusEcharts chartRef={lineChartRef} config={lineConfig} />
      </div>
    </GeneralChart>
  );
};

export default JobFutureAmountCard;
